import { useEffect, useState } from "react";
import { useDataStore } from "@store/user_org";
import { messagerieApi, type UnreadOverview } from "@services/api/messagerie";
import { navigateTo } from "context/navigation.service";

// Les non-lus bougent en permanence : on rafraîchit tant que l'accueil reste ouvert
// et visible. 5 min = la durée du cache serveur : plus souvent ne servirait que le
// même calcul. Onglet masqué : aucun appel ; au retour, un appel si le dernier date.
const REFRESH_MS = 5 * 60 * 1000;

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function MessagesNonLusWidget() {
  const orgMembers = useDataStore((s) => s.orgMembers);
  const [overview, setOverview] = useState<UnreadOverview | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let lastLoad = 0;
    const load = () => {
      if (document.hidden) return;
      lastLoad = Date.now();
      messagerieApi
        .getUnreadOverview()
        .then((data) => {
          if (!cancelled) setOverview(data);
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };
    load();
    const timer = setInterval(load, REFRESH_MS);
    const onVisible = () => {
      if (!document.hidden && Date.now() - lastLoad >= REFRESH_MS) load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const clientCount = overview?.client_unread_discussions ?? 0;
  const byMember = overview?.by_member ?? [];

  return (
    <div className="flex flex-col bg-white border rounded-xl p-4 gap-3 shadow-lg">
      <div className="flex flex-row justify-between items-center">
        <div className="font-semibold text-black">Messages non lus</div>
        <button
          onClick={() => navigateTo({ route: "/messagerie" })}
          className="text-h3 text-neutral-400 hover:text-neutral-600"
        >
          Ouvrir
        </button>
      </div>

      {/* Côté client : le dernier message reçu n'a été lu par personne du garage. */}
      <button
        onClick={() => navigateTo({ route: "/messagerie" })}
        className="flex flex-row items-center gap-3 rounded-lg p-3 text-left hover:bg-neutral-50"
        style={{ background: clientCount > 0 ? "#EEF2FF" : "#F8F8F8" }}
      >
        <span
          className="text-2xl font-semibold"
          style={{ color: clientCount > 0 ? "#4F46E5" : "#999999" }}
        >
          {loading ? "–" : clientCount}
        </span>
        <span className="text-h3 text-neutral-500">
          {clientCount > 1
            ? "conversations clients sans réponse"
            : "conversation client sans réponse"}
        </span>
      </button>

      {/* Côté équipe : combien de conversations chacun n'a pas encore lues. */}
      <div className="flex flex-col">
        <span className="text-h4 text-neutral-400 mb-1">Par membre de l'équipe</span>
        {!loading && byMember.length === 0 && (
          <div className="text-h3 text-neutral-300 py-2">Tout le monde est à jour</div>
        )}
        <div className="flex flex-col divide-y">
          {byMember.map((entry) => {
            const member = orgMembers.find((m) => m.id === entry.user_id);
            const name = member?.infos?.display_name || entry.user_name;
            return (
              <div key={entry.user_id} className="flex flex-row items-center py-2 px-1 gap-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-h4 font-medium flex-shrink-0"
                  style={{ backgroundColor: member?.customization?.color_tag || "#6366F1" }}
                >
                  {member?.customization?.photo_url ? (
                    <img
                      src={member.customization.photo_url}
                      className="w-8 h-8 rounded-full object-cover"
                      alt=""
                    />
                  ) : (
                    getInitials(name || "?")
                  )}
                </div>
                <div className="text-h2 font-medium text-black flex-1 min-w-0 truncate">
                  {name}
                </div>
                <span className="text-h3 font-medium text-indigo-500 flex-shrink-0">
                  {entry.unread_discussions}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
